import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Sparkles, Ticket, ShieldCheck, CreditCard, Store, Lock } from "lucide-react";
import { api } from "../lib/api";
import { purchasePlan, purchaseSellerPlan } from "../lib/payments";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Pricing() {
  const { user, patchUser } = useAuth();
  const navigate = useNavigate();
  const [buyerCfg, setBuyerCfg] = useState({ enabled: false, plans: [], key_id: "" });
  const [sellerCfg, setSellerCfg] = useState({ enabled: false, plans: [], key_id: "" });
  const [buying, setBuying] = useState("");
  const isAdmin = user && user.role === "admin";
  const isSeller = user && user.role === "seller";
  const isBuyer = user && user.role !== "seller" && user.role !== "admin";

  // Role gating: sellers can ONLY buy seller plans; buyers ONLY buyer plans.
  const buyerLocked = !!isSeller;
  const sellerLocked = !!isBuyer;
  const primary = isSeller ? "seller" : "buyer";

  useEffect(() => {
    api.get("/payments/config").then((r) => setBuyerCfg(r.data)).catch(() => {});
    api.get("/payments/seller/plans").then((r) => setSellerCfg(r.data)).catch(() => {});
  }, []);

  const handleBuyBuyer = async (planId) => {
    if (!user) { navigate("/login", { state: { from: "/pricing" } }); return; }
    if (isAdmin) { toast.info("Admin has full access — no plan required."); return; }
    if (buyerLocked) { toast.info("Buyer plans are for buyer accounts only. Sellers can purchase Seller plans below."); return; }
    setBuying(`b-${planId}`);
    try {
      const res = await purchasePlan(planId);
      patchUser({ credits: res.credits, plan: res.plan });
      toast.success(`Payment successful! ${res.added} credits added. You now have ${res.credits} credits.`);
    } catch (e) {
      if (e.message === "CANCELLED") toast.info("Payment cancelled.");
      else toast.error(e.message || "Something went wrong.");
    } finally { setBuying(""); }
  };

  const handleBuySeller = async (planId) => {
    if (!user) { navigate("/login", { state: { from: "/pricing" } }); return; }
    if (isAdmin) { toast.info("Admin has full access — no plan required."); return; }
    if (sellerLocked) { toast.info("Seller plans are for seller accounts only. Buyers can purchase Buyer plans above."); return; }
    setBuying(`s-${planId}`);
    try {
      const res = await purchaseSellerPlan(planId);
      patchUser({ listingLimit: res.listingLimit, sellerPlan: res.plan });
      toast.success(`Payment successful! +${res.added} listings added. Your quota is now ${res.listingLimit}.`);
    } catch (e) {
      if (e.message === "CANCELLED") toast.info("Payment cancelled.");
      else toast.error(e.message || "Something went wrong.");
    } finally { setBuying(""); }
  };

  const buyerSection = (
    <section id="buyer" className={`scroll-mt-24 ${buyerLocked ? "opacity-60" : ""}`}>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <div>
          <p className="text-[11px] font-bold tracking-widest uppercase text-accent">For Buyers</p>
          <h2 className="font-display font-bold text-2xl md:text-3xl text-ink mt-1">Contact Credits</h2>
          <p className="text-sm text-muted-foreground mt-1">Each credit unlocks one seller&apos;s contact — kept forever.</p>
        </div>
        {primary === "buyer" && !buyerLocked && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary whitespace-nowrap">
            <Sparkles className="h-3.5 w-3.5" /> Recommended for you
          </span>
        )}
        {buyerLocked && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full bg-neutral-100 text-neutral-500 whitespace-nowrap" data-testid="buyer-locked-tag">
            <Lock className="h-3.5 w-3.5" /> Seller accounts cannot purchase
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {buyerCfg.plans.map((p) => (
          <div key={p.id} className={`relative bg-white rounded-2xl border shadow-soft p-6 flex flex-col ${p.popular && !buyerLocked ? "border-accent ring-2 ring-accent/30 md:-translate-y-2" : "border-border"}`} data-testid={`buyer-plan-${p.id}`}>
            {p.popular && !buyerLocked && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-accent text-white text-xs font-semibold px-3 py-1 rounded-full shadow whitespace-nowrap">
                <Sparkles className="h-3.5 w-3.5" /> Most Popular
              </span>
            )}
            <h3 className="font-display font-bold text-xl text-ink">{p.label}</h3>
            <p className="text-sm text-muted-foreground">{p.tagline}</p>
            <div className="mt-4 flex items-end gap-1">
              <span className="font-display font-bold text-4xl text-primary">₹{p.price}</span>
            </div>
            <p className="mt-1 text-sm font-medium text-accent">{p.credits} unlock contacts</p>
            <ul className="mt-5 space-y-2.5 flex-1">
              {p.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-ink/80"><Check className="h-4 w-4 text-success mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Button
              onClick={() => handleBuyBuyer(p.id)}
              disabled={!!buying || !buyerCfg.enabled || isAdmin || buyerLocked}
              className={`mt-6 w-full rounded-lg h-11 gap-2 whitespace-nowrap ${p.popular && !buyerLocked ? "bg-accent hover:bg-accent/90" : "bg-primary hover:bg-primary/90"} text-white disabled:opacity-60 disabled:cursor-not-allowed`}
              data-testid={`buy-buyer-${p.id}`}
            >
              {buyerLocked ? (<><Lock className="h-4 w-4 shrink-0" /> Seller account</>)
                : isAdmin ? (<><ShieldCheck className="h-4 w-4 shrink-0" /> Included with Admin</>)
                : buying === `b-${p.id}` ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><CreditCard className="h-4 w-4 shrink-0" /> Buy {p.label}</>}
            </Button>
          </div>
        ))}
      </div>
      {!buyerCfg.enabled && (
        <div className="mt-4 max-w-2xl text-center text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 mx-auto">
          Buyer payments are not configured yet. Add Razorpay keys to enable checkout.
        </div>
      )}
    </section>
  );

  const sellerSection = (
    <section id="seller" className={`scroll-mt-24 ${sellerLocked ? "opacity-60" : ""}`}>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <div>
          <p className="text-[11px] font-bold tracking-widest uppercase text-accent">For Sellers</p>
          <h2 className="font-display font-bold text-2xl md:text-3xl text-ink mt-1">Listing Quota Top-ups</h2>
          <p className="text-sm text-muted-foreground mt-1">Add listings to your seller quota. Purchases stack — never expire.</p>
        </div>
        {primary === "seller" && !sellerLocked && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary whitespace-nowrap">
            <Sparkles className="h-3.5 w-3.5" /> Recommended for you
          </span>
        )}
        {sellerLocked && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full bg-neutral-100 text-neutral-500 whitespace-nowrap" data-testid="seller-locked-tag">
            <Lock className="h-3.5 w-3.5" /> Buyer accounts cannot purchase
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {sellerCfg.plans.map((p) => (
          <div key={p.id} className={`relative bg-white rounded-2xl border shadow-soft p-6 flex flex-col ${p.popular && !sellerLocked ? "border-primary ring-2 ring-primary/25 md:-translate-y-2" : "border-border"}`} data-testid={`seller-plan-${p.id}`}>
            {p.popular && !sellerLocked && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full shadow whitespace-nowrap">
                <Sparkles className="h-3.5 w-3.5" /> Recommended
              </span>
            )}
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary/80"><Store className="h-3.5 w-3.5" /> Seller</div>
            <h3 className="font-display font-bold text-xl text-ink mt-1">{p.label}</h3>
            <p className="text-sm text-muted-foreground">{p.tagline}</p>
            <div className="mt-4 flex items-end gap-1">
              <span className="font-display font-bold text-4xl text-primary">₹{p.price}</span>
            </div>
            <p className="mt-1 text-sm font-medium text-emerald-700">+{p.listings} listings added to your quota</p>
            <ul className="mt-5 space-y-2.5 flex-1">
              {p.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-ink/80"><Check className="h-4 w-4 text-success mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Button
              onClick={() => handleBuySeller(p.id)}
              disabled={!!buying || !sellerCfg.enabled || isAdmin || sellerLocked}
              className={`mt-6 w-full rounded-lg h-11 gap-2 whitespace-nowrap ${p.popular && !sellerLocked ? "bg-primary hover:bg-primary/90" : "bg-neutral-900 hover:bg-neutral-800"} text-white disabled:opacity-60 disabled:cursor-not-allowed`}
              data-testid={`buy-seller-${p.id}`}
            >
              {sellerLocked ? (<><Lock className="h-4 w-4 shrink-0" /> Buyer account</>)
                : isAdmin ? (<><ShieldCheck className="h-4 w-4 shrink-0" /> Included with Admin</>)
                : buying === `s-${p.id}` ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><CreditCard className="h-4 w-4 shrink-0" /> Buy {p.label}</>}
            </Button>
          </div>
        ))}
      </div>
      {!sellerCfg.enabled && (
        <div className="mt-4 max-w-2xl text-center text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 mx-auto">
          Seller payments are not configured yet. Add Razorpay keys to enable checkout.
        </div>
      )}
    </section>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Seo title="Pricing — Buyer & Seller Plans | The Pataudi" description="Buyer contact credits and seller listing top-ups. Pay securely via UPI, cards, net banking and wallets." />
      <Header />

      <main className="flex-1">
        <section className="bg-primary text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 text-center">
            <span className="inline-flex items-center gap-1.5 bg-white/10 text-white text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
              <Ticket className="h-3.5 w-3.5" /> Pricing
            </span>
            <h1 className="mt-4 font-display font-bold text-3xl md:text-5xl tracking-tight">Simple pricing for buyers &amp; sellers</h1>
            <p className="mt-3 text-white/80 max-w-2xl mx-auto">Unlock seller contacts, or top up your listing quota — all in one place. Pay once, use forever.</p>
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              <a href="#buyer" className="inline-flex items-center gap-1.5 text-sm font-semibold bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2 transition-colors whitespace-nowrap">For Buyers</a>
              <a href="#seller" className="inline-flex items-center gap-1.5 text-sm font-semibold bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2 transition-colors whitespace-nowrap">For Sellers</a>
            </div>
            {user && !isAdmin && (
              <div className="mt-6 inline-flex items-center gap-4 bg-white text-primary font-semibold rounded-full px-4 py-2 flex-wrap justify-center max-w-full" data-testid="current-status-banner">
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Ticket className="h-4 w-4 text-accent" /> {user.credits ?? 0} credits</span>
                <span className="h-4 w-px bg-primary/20" />
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Store className="h-4 w-4 text-primary" /> {user.listingLimit ?? 0} listing quota</span>
              </div>
            )}
            {isAdmin && (
              <div className="mt-6 inline-flex items-center gap-2 bg-white text-primary font-semibold rounded-full px-4 py-2 whitespace-nowrap" data-testid="admin-access-banner">
                <ShieldCheck className="h-4 w-4 text-primary" /> Admin — Full access
              </div>
            )}
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-14">
          {primary === "buyer" ? (<>{buyerSection}{sellerSection}</>) : (<>{sellerSection}{buyerSection}</>)}

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-success" /> Secure payments by Razorpay</span>
            <span>UPI · Debit/Credit Card · Net Banking · Wallets</span>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
